import { flow, isNull, set, unset } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { Button } from '../../shared/button/Button';
import { PlusButton } from '../../shared/button/PlusButton';
import { Icon } from '../../shared/icon/Icon';
import { Agency, Bin, Document, Duplicate, Functions, OfficeBuilding, User } from '../../shared/icons';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../shared/tooltip';
import { fetchAllUsersStarted, getAllUsers } from '../users/store';
import { AccessGroupModal } from './AccessGroupModal';
import styles from './AccessGroups.module.scss';
import { AccessGroup, fetchAllAccessGroupsStarted, fetchAllEntitiesForAccessGroupsStarted, fetchDocumentGroupsForAccessGroupsStarted, fetchDocumentNamesForAccessGroupsStarted, getAllAccessGroups, getDeleteConfirmationDialogOpen, getIsFetchingAccessGroups, toggleAccessGroupModal, toggleDeleteAccessGroupConfirmationModal } from './store';
import { fetchAllRolesStarted } from '../roles/store';
import { IconButton } from '../../shared/button/IconButton';
import { Position } from '../../shared/modal/PositionModal';
import { ActionModal } from '../../shared/modal/ActionModal';
import { DeleteAccessGroupConfirmationModal } from './DeleteAccessGroupConfirmationModal';

const { french, grey } = styles;

const NAV_BAR = 100;
const PADDING = 40;
const HEADER = 52;
const DISCLAIMER = 86;
const OFFSET = NAV_BAR + PADDING + HEADER + DISCLAIMER;

export const AccessGroups: React.FC = () => {
    useFetchStarted([fetchAllAccessGroupsStarted(), fetchAllUsersStarted(), fetchAllEntitiesForAccessGroupsStarted(), fetchDocumentNamesForAccessGroupsStarted(), fetchDocumentGroupsForAccessGroupsStarted(), fetchAllRolesStarted()]);

    const dispatch = useAppDispatch();

    const [screenWidth, screenHeight] = useWindowResize();

    const [actionModalOpen, setActionModalOpen] = useState<AccessGroup | null>(null);
    const [position, setPosition] = useState<Position | null>(null);

    const openModal = useCallback((x: number, y: number, accessGroup: AccessGroup) => {
        setPosition({ x, y });
        setActionModalOpen(accessGroup);
    }, []);

    const closeAccessGroupsButtonModal = useCallback(() => {
        setPosition(null);
        setActionModalOpen(null);
    }, []);

    const fetchingAccessGroups = useAppSelector(getIsFetchingAccessGroups);
    const accessGroups = useAppSelector(getAllAccessGroups);
    const allUsers = useAppSelector(getAllUsers);
    const deleteConfirmationDialogOpen = useAppSelector(getDeleteConfirmationDialogOpen);
    const getTotalUsers = useCallback((roleIds: number[], userIds: number[]) => {
        const filteredUsers = allUsers.filter(({ userId, roleId }) => !isNull(roleId) && roleIds.includes(roleId) && !userIds.includes(userId));
        return userIds.concat(filteredUsers.map(({ userId }) => userId));
    }, [allUsers]);

    const scrollableHeight = useMemo(() => screenHeight - OFFSET, [screenHeight]);
    const tileWidth = useMemo(() => screenWidth * .84 / 2, [screenWidth]);

    const toggleModal = useCallback((accessGroup?: AccessGroup) => dispatch(toggleAccessGroupModal(accessGroup)), [dispatch]);
    const duplicate = useCallback((accessGroup: AccessGroup) => {
        const strippedGroup = flow(
            unset('accessGroupId'),
            set('userIds', []),
            set('accessGroupName', ''),
            set('description', ''),
            set('roleIds', [])
        )(accessGroup);
        dispatch(toggleAccessGroupModal(strippedGroup));
    }, [dispatch]);
    const getIconColour = useCallback((ids: number[]) => ids.length > 0 ? french : grey, []);

    const deleteRole = useCallback((accessGroup: AccessGroup) => dispatch(toggleDeleteAccessGroupConfirmationModal(accessGroup)), [dispatch]);
    const closeDeleteConfirmationModal = useCallback(() => dispatch(toggleDeleteAccessGroupConfirmationModal()), [dispatch]);

    const selectedAccessGroup = useCallback((accessGroup: AccessGroup) => [
        { label: 'Duplicate', icon: Duplicate, onClick: () => duplicate(accessGroup), withSeparator: true },
        { label: 'Delete', icon: Bin, onClick: () => deleteRole(accessGroup), isDelete: true }
    ], [deleteRole, duplicate]);

    return (
        <div className={styles.accessGroupsWrapper}>
            <div className={styles.titleContainer}>
                <div className={styles.title}>Access Groups</div>
                <PlusButton onClick={() => toggleModal()} fontSize={22} testId='create-access-group' />
            </div>
            <div className={styles.disclaimer}>
                The access groups is an entirely optional configuration; if no groups are added then all users can see every document in the system.
                However, here you can configure users to only be able to access certain counterparties.
                <strong> Please note:</strong> Once a group has been added, users not assigned to groups will not be able to see any documents or counterparties
            </div>
            <Scrollable height={`${scrollableHeight}px`}>
                <div className={styles.tilesColumnWrapper}>
                    {fetchingAccessGroups ? <div className={styles.loadingWrapper}><LoadingDots /></div> : accessGroups.map((accessGroup, index) => {
                        const { accessGroupName, description, userIds, entityIds, documentNameIds, roleIds } = accessGroup;
                        const breakdown = [
                            { ids: roleIds, title: 'Roles', icon: Agency },
                            { ids: getTotalUsers(roleIds, userIds), title: 'Users', icon: User },
                            { ids: entityIds, title: 'Entities', icon: OfficeBuilding },
                            { ids: documentNameIds, title: 'Documents', icon: Document }
                        ];
                        return (
                            <div key={index} className={styles.accessGroupTileWrapper} style={{ width: `${tileWidth - 40}px` }}>
                                <div className={styles.groupInfoWrapper}>
                                    <div className={styles.groupName}>
                                        <OverflowTooltip overlayText={accessGroupName} />
                                    </div>
                                    <div className={styles.groupDescription}>
                                        <Scrollable>
                                            <>{description}</>
                                        </Scrollable>
                                    </div>
                                </div >
                                <div className={styles.groupUsageWrapper}>
                                    {breakdown.map(({ title, ids, icon }) => (
                                        <div className={styles.breakdownWrapper} key={title}>
                                            <Icon icon={icon} fontSize={50} color={getIconColour(ids)} />
                                            <div className={styles.breakdown}>
                                                <div className={styles.breakdownTitle}>{title}:</div>
                                                <div>{ids.length}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div >
                                <div className={styles.buttonWrapper}>
                                    <IconButton icon={Functions} onClick={e => openModal(e.clientX, e.clientY, accessGroup)} />
                                    {actionModalOpen &&
                                        <ActionModal
                                            isOpen={!isNull(actionModalOpen)}
                                            position={position}
                                            actions={selectedAccessGroup(actionModalOpen)}
                                            closeModal={closeAccessGroupsButtonModal}
                                            testId='access-groups-buttons-modal'
                                        />}
                                    <Button label='Edit' onClick={() => toggleModal(accessGroup)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Scrollable>
            <AccessGroupModal />
            <DeleteAccessGroupConfirmationModal isOpen={deleteConfirmationDialogOpen} closeModal={closeDeleteConfirmationModal} />
        </div>
    );
};
